import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';
import SearchableSelect from './SearchableSelect';

/**
 * Admin → JIRA → Salesforce Sync configuration page (Phase 3).
 *
 * One-way sync: when a JIRA issue's status changes, a JIRA Automation rule POSTs
 * to /api/sync/jira with the issue's Salesforce Case Number; Lyric looks up that
 * Case and updates its Status field. This page lets an admin:
 *   1. see whether LIVE Salesforce writes are ready (Phase-2 creds),
 *   2. edit the config — status map (JIRA status → Salesforce Case Status value)
 *      and the JIRA field that holds the Salesforce Case Number,
 *   3. dry-run or LIVE-test a sync two ways: "By JIRA status" (translated
 *      through the status map) or "By Salesforce status" (a direct value that
 *      bypasses the map). Both the Case Status and Case Number fields are
 *      searchable pickers (SearchableSelect) that remember recent selections;
 *      Dry-run makes no Salesforce call (works with zero SF/AWS credentials);
 *      LIVE actually updates the Case in Salesforce,
 *   4. read the audit log of recent sync attempts.
 *
 * Backend: server/routes/sync.js
 *   GET/PUT /api/sync/config · GET /api/sync/log · POST /api/sync/test ·
 *   POST /api/sync/test-live · GET /api/sync/auth-status ·
 *   GET /api/sync/case-status-values · GET /api/sync/cases
 */

// The status map is edited as an array of pairs (easy add/remove); we convert
// to/from the server's object shape (statusMap) on load/save. Each row carries a
// stable `id` used as the React key so removing/reordering rows never makes
// React reuse the wrong <input> DOM node (which would mix values between rows).
let rowSeq = 0;
const rowId = () => (
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `row-${Date.now()}-${rowSeq++}`
);
const emptyMapRow = () => ({ id: rowId(), jira: '', sf: '' });

function mapObjToRows(obj) {
  const rows = Object.entries(obj || {}).map(([jira, sf]) => ({ id: rowId(), jira, sf }));
  return rows.length ? rows : [emptyMapRow()];
}
function rowsToMapObj(rows) {
  const out = {};
  for (const { jira, sf } of rows) {
    const k = String(jira || '').trim();
    const v = String(sf || '').trim();
    if (k && v) out[k] = v;
  }
  return out;
}

export default function AdminSalesforceSync() {
  const { oktaAuth } = useOktaAuth() || {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState(null);
  const [notice, setNotice] = useState('');

  // Live-write readiness (Phase 2).
  const [authStatus, setAuthStatus] = useState(null);

  // Config form state.
  const [enabled, setEnabled] = useState(true);
  const [mapRows, setMapRows] = useState([emptyMapRow()]);
  const [jiraCaseNumberField, setJiraCaseNumberField] = useState('');
  const [sfStatusField, setSfStatusField] = useState('Status');
  const [sfApiVersion, setSfApiVersion] = useState('v60.0');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  // Tester: 'jira' resolves via the status map; 'direct' sets an SF status value.
  const [testMode, setTestMode] = useState('jira');
  const [testStatus, setTestStatus] = useState('');
  const [testSfStatus, setTestSfStatus] = useState('');
  const [testCaseNumber, setTestCaseNumber] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Live tester (actually writes to Salesforce).
  const [liveTesting, setLiveTesting] = useState(false);
  const [liveResult, setLiveResult] = useState(null);

  // Audit log.
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // JIRA field finder (helps discover the Case Number custom-field id).
  const [showFinder, setShowFinder] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [projectQuery, setProjectQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldsMeta, setFieldsMeta] = useState(null);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState('');
  const [fieldQuery, setFieldQuery] = useState('');
  const [customOnly, setCustomOnly] = useState(true);
  const [projectOnly, setProjectOnly] = useState(true);

  // Live Case Status picklist from Salesforce (for building the map).
  const [statusValues, setStatusValues] = useState([]);
  const [statusValuesLoading, setStatusValuesLoading] = useState(false);
  const [statusValuesError, setStatusValuesError] = useState('');
  const [statusValuesLoaded, setStatusValuesLoaded] = useState(false);

  // Live Case search results (for the tester's Case-Number picker).
  const [caseOptions, setCaseOptions] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);

  const authHeader = useCallback(async () => {
    let token = '';
    try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [oktaAuth]);

  const apiGet = useCallback(async (url) => {
    const res = await fetch(url, { headers: await authHeader() });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setErrorDetail({ status: res.status, ...body });
      throw new Error(body?.error || `HTTP ${res.status}`);
    }
    return body;
  }, [authHeader]);

  const loadConfig = useCallback((cfg) => {
    setEnabled(cfg.enabled !== false);
    setMapRows(mapObjToRows(cfg.statusMap));
    setJiraCaseNumberField(cfg.jiraCaseNumberField || '');
    setSfStatusField(cfg.sfStatusField || 'Status');
    setSfApiVersion(cfg.sfApiVersion || 'v60.0');
    setUpdatedAt(cfg.updatedAt || null);
    setUpdatedBy(cfg.updatedBy || null);
  }, []);

  const refreshLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const body = await apiGet('/api/sync/log?limit=50');
      setLog(body.entries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLogLoading(false);
    }
  }, [apiGet]);

  // Initial load: config + auth-status + log in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setErrorDetail(null);
      try {
        const [cfgBody, authBody] = await Promise.all([
          apiGet('/api/sync/config'),
          apiGet('/api/sync/auth-status').catch(() => null),
        ]);
        if (cancelled) return;
        loadConfig(cfgBody.config || {});
        if (authBody) setAuthStatus(authBody);
        await refreshLog();
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiGet, loadConfig, refreshLog]);

  // ── Status-map row helpers ────────────────────────────────────────────────
  const setMapRow = (idx, field, value) => setMapRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const addMapRow = () => setMapRows((rows) => [...rows, emptyMapRow()]);
  const removeMapRow = (idx) => setMapRows((rows) => (rows.length === 1 ? [emptyMapRow()] : rows.filter((_, i) => i !== idx)));

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    setErrorDetail(null);
    setNotice('');
    try {
      const payload = {
        enabled,
        statusMap: rowsToMapObj(mapRows),
        jiraCaseNumberField: jiraCaseNumberField.trim(),
        sfStatusField: sfStatusField.trim() || 'Status',
        sfApiVersion: sfApiVersion.trim() || 'v60.0',
      };
      const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
      const res = await fetch('/api/sync/config', { method: 'PUT', headers, body: JSON.stringify(payload) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorDetail({ status: res.status, ...body });
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      loadConfig(body.config || {});
      setNotice('Configuration saved. Changes take effect immediately — no redeploy needed.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [enabled, mapRows, jiraCaseNumberField, sfStatusField, sfApiVersion, authHeader, loadConfig]);

  // Build the tester payload for the current mode: 'direct' sends an explicit
  // Salesforce Status value (bypassing the map); 'jira' sends the JIRA status.
  const testPayload = useCallback(() => (
    testMode === 'direct'
      ? { sfStatusValue: testSfStatus.trim(), caseNumber: testCaseNumber.trim() }
      : { jiraStatus: testStatus.trim(), caseNumber: testCaseNumber.trim() }
  ), [testMode, testSfStatus, testStatus, testCaseNumber]);

  const runTest = useCallback(async () => {
    setTesting(true);
    setError('');
    setTestResult(null);
    try {
      const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
      const res = await fetch('/api/sync/test', {
        method: 'POST',
        headers,
        body: JSON.stringify(testPayload()),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorDetail({ status: res.status, ...body });
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      setTestResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setTesting(false);
    }
  }, [testPayload, authHeader]);

  // LIVE test: actually PATCHes the Salesforce Case. Guarded by a confirm()
  // because it mutates real Salesforce data. Refreshes the audit log after.
  const runLiveTest = useCallback(async () => {
    const caseNum = testCaseNumber.trim();
    const isDirect = testMode === 'direct';
    const jira = testStatus.trim();
    const sfVal = testSfStatus.trim();
    if (!caseNum || (isDirect ? !sfVal : !jira)) {
      setError(isDirect
        ? 'Pick a Salesforce Case Status and enter a Case Number to run a live update.'
        : 'Enter both a JIRA status and a Salesforce Case Number to run a live update.');
      return;
    }
    const target = isDirect
      ? `to "${sfVal}"`
      : `to the mapped value for "${jira}"`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `This will UPDATE Salesforce Case #${caseNum} — setting its ${sfStatusField || 'Status'} `
      + `${target}. This changes real Salesforce data. Continue?`,
    )) return;
    setLiveTesting(true);
    setError('');
    setErrorDetail(null);
    setLiveResult(null);
    try {
      const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
      const res = await fetch('/api/sync/test-live', {
        method: 'POST',
        headers,
        body: JSON.stringify(testPayload()),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorDetail({ status: res.status, ...body });
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      setLiveResult(body);
      refreshLog();
    } catch (e) {
      setError(e.message);
    } finally {
      setLiveTesting(false);
    }
  }, [testMode, testStatus, testSfStatus, testCaseNumber, sfStatusField, testPayload, authHeader, refreshLog]);

  // ── JIRA field finder ───────────────────────────────────────────────────
  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError('');
    try {
      const body = await apiGet('/api/projects');
      if (body.requiresAuth || body.success === false) {
        setProjects([]);
        setProjectsError(body.message || 'Connect your JIRA account first (open a JIRA tool to authenticate), then retry.');
      } else {
        setProjects(body.projects || []);
      }
    } catch (e) {
      setProjectsError(e.message);
    } finally {
      setProjectsLoading(false);
    }
  }, [apiGet]);

  const loadFields = useCallback(async (projKey) => {
    setFieldsLoading(true);
    setFieldsError('');
    setFields([]);
    setFieldsMeta(null);
    try {
      const body = await apiGet(`/api/jira/project/${encodeURIComponent(projKey)}/fields`);
      setFields(body.fields || []);
      setFieldsMeta({ projectScopeAvailable: body.projectScopeAvailable, counts: body.counts || {} });
    } catch (e) {
      setFieldsError(e.message);
    } finally {
      setFieldsLoading(false);
    }
  }, [apiGet]);

  const toggleFinder = () => {
    setShowFinder((s) => {
      const next = !s;
      if (next && projects.length === 0 && !projectsLoading) loadProjects();
      return next;
    });
  };

  const selectProject = (p) => {
    setSelectedProject({ key: p.key, name: p.name });
    setPickerOpen(false);
    setProjectQuery('');
    setFieldQuery('');
    loadFields(p.key);
  };

  const applyFieldId = (fid) => {
    setJiraCaseNumberField(fid);
    setNotice(`Set the Case Number field to ${fid}. Click "Save configuration" above to persist it.`);
  };

  const copyId = (fid) => {
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(fid);
      setNotice(`Copied ${fid} to the clipboard.`);
    } catch { /* clipboard may be unavailable */ }
  };

  const loadStatusValues = useCallback(async () => {
    setStatusValuesLoading(true);
    setStatusValuesError('');
    try {
      const body = await apiGet('/api/sync/case-status-values');
      setStatusValues(body.values || []);
      setStatusValuesLoaded(true);
    } catch (e) {
      setStatusValues([]);
      setStatusValuesError(e.message);
    } finally {
      setStatusValuesLoading(false);
    }
  }, [apiGet]);

  // Server-side Case search for the tester's Case-Number picker. Called
  // (debounced) by SearchableSelect as the admin types; '' loads recent Cases.
  const searchCases = useCallback(async (term) => {
    setCasesLoading(true);
    try {
      const qs = term ? `?limit=25&q=${encodeURIComponent(term)}` : '?limit=25';
      const body = await apiGet(`/api/sync/cases${qs}`);
      const cases = Array.isArray(body.cases) ? body.cases : [];
      setCaseOptions(cases.map((c) => ({
        value: c.caseNumber,
        label: c.caseNumber,
        sublabel: [c.status, c.subject].filter(Boolean).join(' · '),
      })));
    } catch {
      // Search failures (e.g. SF not connected) just yield no options; the
      // picker still allows free-text entry of a Case Number.
      setCaseOptions([]);
    } finally {
      setCasesLoading(false);
    }
  }, [apiGet]);

  const outcomeBadge = (outcome) => {
    const map = {
      success: styles.badgeOk,
      skipped: styles.badgeSkip,
      error: styles.badgeFail,
    };
    return <span style={map[outcome] || styles.badgeSkip}>{outcome || '—'}</span>;
  };

  const projectFilter = projectQuery.trim().toLowerCase();
  const filteredProjects = projects.filter((p) => {
    if (!projectFilter) return true;
    return `${p.key || ''} ${p.name || ''}`.toLowerCase().includes(projectFilter);
  }).slice(0, 60);

  const scoped = Boolean(fieldsMeta && fieldsMeta.projectScopeAvailable);
  const fieldFilter = fieldQuery.trim().toLowerCase();
  const visibleFields = fields.filter((f) => {
    if (customOnly && !f.custom) return false;
    if (projectOnly && scoped && f.onProject === false) return false;
    if (!fieldFilter) return true;
    return f.name.toLowerCase().includes(fieldFilter) || f.id.toLowerCase().includes(fieldFilter);
  });

  const activeStatusValues = statusValues.filter((v) => v.active !== false);

  // When the tester switches to "direct" mode, auto-load the Case Status
  // picklist once so the searchable picker has real values to offer.
  useEffect(() => {
    if (testMode === 'direct' && !statusValuesLoaded && !statusValuesLoading && !statusValuesError) {
      loadStatusValues();
    }
  }, [testMode, statusValuesLoaded, statusValuesLoading, statusValuesError, loadStatusValues]);

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h2 style={styles.title}>🔄 JIRA → Salesforce Sync</h2>
        <AdminMenu />
      </div>
      <p style={styles.subtitle}>
        One-way sync: when a JIRA issue&apos;s status changes, Lyric looks up the linked
        Salesforce <strong>Case</strong> by its Case Number and updates its{' '}
        <code>{sfStatusField || 'Status'}</code> field. Configure the mapping below,{' '}
        <strong>dry-run</strong> it without touching Salesforce, and watch the audit log. Config
        changes take effect immediately — no redeploy.
      </p>

      {error && (
        <div style={styles.error}>
          {error}
          {errorDetail && errorDetail.status === 403 && (
            <div style={{ marginTop: '6px', fontSize: '12px' }}>
              This page is admin-only. Signed-in user:{' '}
              <code>{errorDetail.currentUser || 'unknown'}</code>
              {errorDetail.reason ? <> · reason: <code>{errorDetail.reason}</code></> : null}
              {errorDetail.hint ? <div style={{ marginTop: '4px', color: '#f0d9a0' }}>{errorDetail.hint}</div> : null}
            </div>
          )}
        </div>
      )}
      {notice && <div style={styles.notice}>{notice}</div>}

      {/* ── Live-write readiness ────────────────────────────────────────────── */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Live Salesforce writes</div>
        {authStatus == null ? (
          <div style={styles.muted}>Checking…</div>
        ) : authStatus.configured ? (
          <div style={styles.readyOk}>
            ✅ <strong>Ready.</strong> Salesforce credentials are provisioned
            {authStatus.flow ? <> (flow: <code>{authStatus.flow}</code>)</> : null}
            {authStatus.loginUrl ? <> · org <code>{authStatus.loginUrl}</code></> : null}. Real
            Case updates will occur when a sync runs.
          </div>
        ) : (
          <div style={styles.readyPending}>
            ⏳ <strong>Not connected yet.</strong> No Salesforce credentials are configured, so live
            writes are disabled. You can still edit config and run <strong>dry-run</strong> tests
            below — nothing is written to Salesforce. To enable live writes, provision the Connected
            App creds per <code>docs/salesforce-sync-setup.md</code>.
          </div>
        )}
      </div>

      {loading ? (
        <div style={styles.muted}>Loading configuration…</div>
      ) : (
        <>
          {/* ── Configuration ─────────────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Configuration</div>

            <label style={styles.toggleRow}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span><strong>Sync enabled</strong> — when off, incoming webhooks are accepted but no Salesforce write happens.</span>
            </label>

            <div style={styles.fieldLabel}>Status mapping (JIRA status → Salesforce Case <code>{sfStatusField || 'Status'}</code> value)</div>
            <div style={styles.hint}>
              JIRA status names are matched case-insensitively. A JIRA status with no row here is a
              deliberate no-op (skipped, not an error).
            </div>
            <div style={styles.statusValuesRow}>
              <button
                type="button"
                style={styles.btn}
                onClick={loadStatusValues}
                disabled={statusValuesLoading}
              >
                {statusValuesLoading
                  ? 'Loading…'
                  : (statusValuesLoaded ? '↻ Reload Case Status values' : 'Load Case Status values from Salesforce')}
              </button>
              {statusValuesLoaded && !statusValuesError && (
                <span style={styles.hintInline}>
                  {activeStatusValues.length} value{activeStatusValues.length === 1 ? '' : 's'} — pick from the
                  dropdown in the “Salesforce Case Status” cells. ★ = default.
                </span>
              )}
            </div>
            {statusValuesError && <div style={styles.errorInline}>{statusValuesError}</div>}
            {statusValuesLoaded && activeStatusValues.length > 0 && (
              <div style={styles.chipWrap}>
                {activeStatusValues.map((v) => (
                  <button
                    type="button"
                    key={v.value}
                    style={styles.chip}
                    title="Copy value"
                    onClick={() => copyId(v.value)}
                  >
                    {v.value}{v.defaultValue ? ' ★' : ''}
                  </button>
                ))}
              </div>
            )}
            <datalist id="sf-case-status-values">
              {activeStatusValues.map((v) => (
                <option key={v.value} value={v.value}>{v.label !== v.value ? v.label : undefined}</option>
              ))}
            </datalist>
            <table style={styles.mapTable}>
              <thead>
                <tr>
                  <th style={styles.th}>JIRA status</th>
                  <th style={styles.thArrow} />
                  <th style={styles.th}>Salesforce Case Status</th>
                  <th style={styles.thAction} />
                </tr>
              </thead>
              <tbody>
                {mapRows.map((row, idx) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <input
                        style={styles.input}
                        value={row.jira}
                        placeholder="e.g. In Progress"
                        onChange={(e) => setMapRow(idx, 'jira', e.target.value)}
                      />
                    </td>
                    <td style={styles.tdArrow}>→</td>
                    <td style={styles.td}>
                      <input
                        style={styles.input}
                        value={row.sf}
                        placeholder="e.g. TBD"
                        list="sf-case-status-values"
                        onChange={(e) => setMapRow(idx, 'sf', e.target.value)}
                      />
                    </td>
                    <td style={styles.tdAction}>
                      <button type="button" style={styles.btnDanger} onClick={() => removeMapRow(idx)} title="Remove row">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" style={styles.btn} onClick={addMapRow}>+ Add mapping</button>

            <div style={styles.fieldLabel}>JIRA field holding the Salesforce Case Number</div>
            <div style={styles.hint}>
              e.g. <code>customfield_12345</code>. This field on the JIRA issue holds the Salesforce
              Case Number (e.g. <code>00001234</code>). Leave blank if the webhook sends the Case
              Number at the top level of the payload.
            </div>
            <input style={styles.input} value={jiraCaseNumberField} placeholder="customfield_12345" onChange={(e) => setJiraCaseNumberField(e.target.value)} />

            <button type="button" style={styles.linkBtn} onClick={() => setShowAdvanced((s) => !s)}>
              {showAdvanced ? '▾' : '▸'} Advanced
            </button>
            {showAdvanced && (
              <div style={styles.advancedBox}>
                <div style={styles.grid2}>
                  <div>
                    <div style={styles.fieldLabel}>Salesforce Case field to update</div>
                    <div style={styles.hint}>The API name of the Case field that receives the mapped value. Default <code>Status</code>.</div>
                    <input style={styles.input} value={sfStatusField} placeholder="Status" onChange={(e) => setSfStatusField(e.target.value)} />
                  </div>
                  <div>
                    <div style={styles.fieldLabel}>Salesforce API version</div>
                    <div style={styles.hint}>REST API version used for the lookup and write.</div>
                    <input style={styles.input} value={sfApiVersion} placeholder="v60.0" onChange={(e) => setSfApiVersion(e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            <div style={styles.saveRow}>
              <button type="button" style={saving ? styles.btnPrimaryDisabled : styles.btnPrimary} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save configuration'}
              </button>
              {updatedAt && (
                <span style={styles.muted}>
                  Last saved {new Date(updatedAt).toLocaleString()}{updatedBy ? ` by ${updatedBy}` : ''}
                </span>
              )}
            </div>
          </div>

          {/* ── JIRA field finder ─────────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionTitleRow}>
              <span style={styles.sectionTitle}>Find the JIRA Case Number field</span>
              <button type="button" style={styles.btn} onClick={toggleFinder}>
                {showFinder ? 'Hide' : 'Open field finder'}
              </button>
            </div>
            <div style={styles.hint}>
              Not sure of the custom-field id? Pick a JIRA project to browse its fields, filter for
              the one that holds the Salesforce Case Number, then click <strong>Use this field</strong>{' '}
              to drop its id into the config above.
            </div>

            {showFinder && (
              <>
                <div style={styles.fieldLabel}>JIRA project</div>
                {projectsError && (
                  <div style={styles.error}>
                    {projectsError}{' '}
                    <button type="button" style={styles.linkBtnInline} onClick={loadProjects}>Retry</button>
                  </div>
                )}
                <div style={styles.pickerWrap}>
                  <input
                    style={styles.input}
                    placeholder={
                      projectsLoading
                        ? 'Loading projects…'
                        : (selectedProject ? `${selectedProject.key} — ${selectedProject.name}` : 'Search projects by key or name…')
                    }
                    value={projectQuery}
                    onChange={(e) => { setProjectQuery(e.target.value); setPickerOpen(true); }}
                    onFocus={() => { setPickerOpen(true); if (projects.length === 0 && !projectsLoading) loadProjects(); }}
                  />
                  {pickerOpen && (
                    <div style={styles.pickerBox}>
                      {filteredProjects.length === 0 ? (
                        <div style={styles.pickerEmpty}>{projectsLoading ? 'Loading…' : 'No matching projects'}</div>
                      ) : filteredProjects.map((p) => (
                        <button type="button" key={p.key || p.id} style={styles.pickerItem} onClick={() => selectProject(p)}>
                          <strong>{p.key}</strong> — {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedProject && (
                  <div style={styles.finderResults}>
                    {fieldsLoading ? (
                      <div style={styles.muted}>Loading fields for {selectedProject.key}…</div>
                    ) : fieldsError ? (
                      <div style={styles.error}>{fieldsError}</div>
                    ) : (
                      <>
                        <div style={styles.filterRow}>
                          <input
                            style={{ ...styles.input, flex: '1 1 240px' }}
                            placeholder="Filter by name or id (e.g. case, salesforce)…"
                            value={fieldQuery}
                            onChange={(e) => setFieldQuery(e.target.value)}
                          />
                          <label style={styles.checkboxRow}>
                            <input type="checkbox" checked={customOnly} onChange={(e) => setCustomOnly(e.target.checked)} /> Custom only
                          </label>
                          {scoped && (
                            <label style={styles.checkboxRow}>
                              <input type="checkbox" checked={projectOnly} onChange={(e) => setProjectOnly(e.target.checked)} /> On this project
                            </label>
                          )}
                        </div>
                        {!scoped && (
                          <div style={styles.hint}>
                            Project-scoped field list unavailable (JIRA permissions/config) — showing all
                            instance fields. A custom field&apos;s id is the same across every project.
                          </div>
                        )}
                        <div style={styles.fieldTableWrap}>
                          <table style={styles.logTable}>
                            <thead>
                              <tr>
                                <th style={styles.th}>Field name</th>
                                <th style={styles.th}>Field id</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th} />
                              </tr>
                            </thead>
                            <tbody>
                              {visibleFields.length === 0 ? (
                                <tr><td style={styles.tdLog} colSpan={4}><span style={styles.muted}>No fields match your filter.</span></td></tr>
                              ) : visibleFields.map((f) => (
                                <tr key={f.id}>
                                  <td style={styles.tdLog}>
                                    {f.name}
                                    {f.custom && <span style={styles.badgeCustom}>custom</span>}
                                    {f.onProject && <span style={styles.badgeProject}>on project</span>}
                                  </td>
                                  <td style={styles.tdLog}><code style={styles.codePill}>{f.id}</code></td>
                                  <td style={styles.tdLog}>{f.type}</td>
                                  <td style={styles.tdLogActions}>
                                    <button type="button" style={styles.btn} onClick={() => copyId(f.id)}>Copy</button>
                                    <button type="button" style={styles.btnPrimarySm} onClick={() => applyFieldId(f.id)}>Use this field</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={styles.muted}>
                          Showing {visibleFields.length} of {fields.length} fields
                          {fieldsMeta && fieldsMeta.counts && fieldsMeta.counts.custom != null ? ` · ${fieldsMeta.counts.custom} custom` : ''}
                          {scoped && fieldsMeta.counts.onProject != null ? ` · ${fieldsMeta.counts.onProject} on ${selectedProject.key}` : ''}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Tester (dry-run + live) ───────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Test a sync</div>
            <div style={styles.hint}>
              <strong>Run dry-run</strong> resolves what would happen — <strong>no Salesforce call
              is made</strong> (works with zero credentials). <strong>Run LIVE update</strong>{' '}
              actually calls Salesforce and writes the Status to that Case (needs live credentials;
              changes real data).
            </div>

            <div style={styles.modeTabs}>
              <button
                type="button"
                style={testMode === 'jira' ? styles.modeTabActive : styles.modeTab}
                onClick={() => setTestMode('jira')}
              >
                By JIRA status (uses the map)
              </button>
              <button
                type="button"
                style={testMode === 'direct' ? styles.modeTabActive : styles.modeTab}
                onClick={() => setTestMode('direct')}
              >
                By Salesforce status (direct)
              </button>
            </div>
            <div style={styles.hint}>
              {testMode === 'jira'
                ? 'Simulates the real webhook: the JIRA status is translated to a Salesforce Case Status via your map above.'
                : 'Skips the map — sets the Salesforce Case Status you pick directly on the Case. Handy for verifying the write itself.'}
            </div>

            <div style={styles.grid2}>
              {testMode === 'jira' ? (
                <div>
                  <div style={styles.fieldLabel}>JIRA status</div>
                  <input style={styles.input} value={testStatus} placeholder="In Progress" onChange={(e) => setTestStatus(e.target.value)} />
                </div>
              ) : (
                <div>
                  <div style={styles.fieldLabel}>Salesforce Case Status</div>
                  <SearchableSelect
                    value={testSfStatus}
                    onChange={setTestSfStatus}
                    options={activeStatusValues.map((v) => ({
                      value: v.value,
                      label: v.value,
                      sublabel: v.defaultValue ? 'default' : (v.label !== v.value ? v.label : ''),
                    }))}
                    recentsKey="lyric.sync.recentSfStatuses"
                    placeholder="Search Case Status…"
                    emptyHint={
                      statusValuesLoading ? 'Loading Case Status values…'
                        : (statusValuesError ? `Couldn't load statuses — type a value` : 'No matching status')
                    }
                  />
                </div>
              )}
              <div>
                <div style={styles.fieldLabel}>Salesforce Case Number</div>
                <SearchableSelect
                  value={testCaseNumber}
                  onChange={setTestCaseNumber}
                  options={caseOptions}
                  onSearch={searchCases}
                  loading={casesLoading}
                  recentsKey="lyric.sync.recentCaseNumbers"
                  placeholder="Search Case number or subject…"
                  emptyHint="Type to search Salesforce Cases (or enter a number directly)"
                />
              </div>
            </div>
            <div style={styles.testerButtons}>
              <button type="button" style={testing ? styles.btnPrimaryDisabled : styles.btnPrimary} onClick={runTest} disabled={testing}>
                {testing ? 'Resolving…' : 'Run dry-run'}
              </button>
              <button
                type="button"
                style={liveTesting ? styles.btnPrimaryDisabled : styles.btnLive}
                onClick={runLiveTest}
                disabled={liveTesting}
                title="Actually update the Salesforce Case (writes real data)"
              >
                {liveTesting ? 'Updating Salesforce…' : '⚡ Run LIVE update'}
              </button>
            </div>

            {testResult && (
              <div style={styles.testResult}>
                <div style={{ marginBottom: '6px' }}>
                  {testResult.wouldSync
                    ? <span style={styles.badgeOk}>WOULD SYNC</span>
                    : <span style={styles.badgeSkip}>WOULD SKIP</span>}
                </div>
                {testResult.wouldSync ? (
                  <div>
                    Would set Case <code>{testResult.sfStatusField}</code>{' '}
                    = <code>{testResult.sfStatusValue}</code> on Case #<code>{testResult.caseNumber}</code>.
                  </div>
                ) : (
                  <div style={styles.muted}>No write would occur.</div>
                )}
                {Array.isArray(testResult.notes) && testResult.notes.length > 0 && (
                  <ul style={styles.notes}>
                    {testResult.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
              </div>
            )}

            {liveResult && (
              <div style={styles.testResult}>
                <div style={{ marginBottom: '6px' }}>
                  {liveResult.performed
                    ? <span style={styles.badgeOk}>SYNCED (LIVE)</span>
                    : <span style={styles.badgeSkip}>SKIPPED — nothing written</span>}
                </div>
                {liveResult.performed ? (
                  <div>
                    Set Case <code>{liveResult.sfStatusField}</code>{' '}
                    = <code>{liveResult.sfStatusValue}</code> on Case #<code>{liveResult.caseNumber}</code>
                    {liveResult.caseId ? <> (Id <code>{liveResult.caseId}</code>)</> : null}.
                  </div>
                ) : (
                  <div style={styles.muted}>No Salesforce write occurred.</div>
                )}
                {Array.isArray(liveResult.notes) && liveResult.notes.length > 0 && (
                  <ul style={styles.notes}>
                    {liveResult.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* ── Audit log ─────────────────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionTitleRow}>
              <span style={styles.sectionTitle}>Recent sync attempts</span>
              <button type="button" style={styles.btn} onClick={refreshLog} disabled={logLoading}>
                {logLoading ? 'Refreshing…' : '↻ Refresh'}
              </button>
            </div>
            {log.length === 0 ? (
              <div style={styles.muted}>No sync attempts logged yet.</div>
            ) : (
              <table style={styles.logTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>When</th>
                    <th style={styles.th}>JIRA</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>→ Case Status</th>
                    <th style={styles.th}>Case #</th>
                    <th style={styles.th}>Case Id</th>
                    <th style={styles.th}>Outcome</th>
                    <th style={styles.th}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((e) => (
                    <tr key={e.id}>
                      <td style={styles.tdLog}>{e.at ? new Date(e.at).toLocaleString() : '—'}</td>
                      <td style={styles.tdLog}>{e.jiraKey || '—'}</td>
                      <td style={styles.tdLog}>{e.jiraStatus || '—'}</td>
                      <td style={styles.tdLog}>{e.sfStatusValue || '—'}</td>
                      <td style={styles.tdLog}>{e.caseNumber || '—'}</td>
                      <td style={styles.tdLog}>{e.caseId ? <span style={styles.subId}>{e.caseId}</span> : '—'}</td>
                      <td style={styles.tdLog}>{outcomeBadge(e.outcome)}</td>
                      <td style={styles.tdLogMsg}>{e.message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
  pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { margin: 0, fontSize: '22px' },
  subtitle: { color: '#9fb0bd', fontSize: '13px', lineHeight: 1.5, maxWidth: '820px' },
  section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
  sectionTitle: { fontSize: '15px', fontWeight: 700, color: '#e8eef3', marginBottom: '10px' },
  sectionTitleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
  toggleRow: { display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#cdd7de', marginBottom: '14px' },
  fieldLabel: { fontSize: '13px', fontWeight: 600, color: '#e8eef3', marginTop: '14px', marginBottom: '2px' },
  hint: { fontSize: '11.5px', color: '#8a98a3', marginBottom: '6px', lineHeight: 1.4 },
  input: {
    padding: '7px 10px', border: '1px solid #33404a', borderRadius: '4px',
    background: '#161a1e', color: '#fff', width: '100%', boxSizing: 'border-box', fontSize: '13px',
  },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px 20px', marginTop: '4px' },
  mapTable: { width: '100%', borderCollapse: 'collapse', marginBottom: '8px' },
  logTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th: { textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#7d8b96', padding: '4px 8px', borderBottom: '1px solid #2f3a44' },
  thArrow: { width: '28px' },
  thAction: { width: '40px' },
  td: { padding: '4px 8px', verticalAlign: 'middle' },
  tdArrow: { padding: '4px 6px', textAlign: 'center', color: '#7d8b96' },
  tdAction: { padding: '4px 8px', textAlign: 'center' },
  tdLog: { padding: '6px 8px', borderBottom: '1px solid #232a31', verticalAlign: 'top', whiteSpace: 'nowrap' },
  tdLogMsg: { padding: '6px 8px', borderBottom: '1px solid #232a31', verticalAlign: 'top', color: '#b8c4cd' },
  subId: { fontSize: '11px', color: '#7d8b96', fontFamily: 'monospace' },
  advancedBox: { marginTop: '8px', padding: '12px', background: '#161a1e', border: '1px dashed #33404a', borderRadius: '4px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '14px', marginTop: '18px' },
  btn: { padding: '7px 12px', background: '#2f3a44', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12.5px' },
  btnPrimary: { minWidth: '180px', textAlign: 'center', padding: '9px 18px', background: '#0052cc', color: '#fff', border: '1px solid transparent', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', boxSizing: 'border-box' },
  btnLive: { minWidth: '180px', textAlign: 'center', padding: '9px 18px', background: '#8a3b00', color: '#ffe0b2', border: '1px solid #d17a1a', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', boxSizing: 'border-box' },
  testerButtons: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '14px' },
  modeTabs: { display: 'inline-flex', gap: '0', margin: '4px 0 6px', border: '1px solid #ccd3dc', borderRadius: '6px', overflow: 'hidden' },
  modeTab: { padding: '7px 14px', background: '#f4f6f9', color: '#42526e', border: 'none', borderRight: '1px solid #ccd3dc', cursor: 'pointer', fontSize: '13px', fontWeight: 500 },
  modeTabActive: { padding: '7px 14px', background: '#0052cc', color: '#fff', border: 'none', borderRight: '1px solid #0052cc', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  btnPrimaryDisabled: { minWidth: '180px', textAlign: 'center', padding: '9px 18px', background: '#243447', color: '#8aa', border: '1px solid transparent', borderRadius: '4px', cursor: 'not-allowed', fontWeight: 600, fontSize: '13px', boxSizing: 'border-box' },
  btnDanger: { padding: '4px 9px', background: '#5a1a1a', color: '#ffcdd2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  linkBtn: { marginTop: '16px', padding: 0, background: 'none', color: '#6bb1ff', border: 'none', cursor: 'pointer', fontSize: '12.5px' },
  linkBtnInline: { padding: 0, background: 'none', color: '#ffd7d7', border: 'none', cursor: 'pointer', fontSize: '12.5px', textDecoration: 'underline' },
  pickerWrap: { position: 'relative', marginTop: '4px' },
  pickerBox: { position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: '2px', maxHeight: '260px', overflowY: 'auto', background: '#161a1e', border: '1px solid #33404a', borderRadius: '4px', boxShadow: '0 6px 20px rgba(0,0,0,0.4)' },
  pickerItem: { display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'none', border: 'none', borderBottom: '1px solid #232a31', color: '#cdd7de', cursor: 'pointer', fontSize: '13px' },
  pickerEmpty: { padding: '8px 10px', color: '#8a98a3', fontSize: '12.5px' },
  finderResults: { marginTop: '12px' },
  filterRow: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px', flexWrap: 'wrap' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#cdd7de', whiteSpace: 'nowrap' },
  fieldTableWrap: { maxHeight: '360px', overflowY: 'auto', border: '1px solid #2f3a44', borderRadius: '4px' },
  tdLogActions: { padding: '6px 8px', borderBottom: '1px solid #232a31', textAlign: 'right', whiteSpace: 'nowrap' },
  btnPrimarySm: { padding: '4px 10px', marginLeft: '6px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  codePill: { fontFamily: 'monospace', fontSize: '12px', background: '#0d1117', padding: '2px 6px', borderRadius: '4px', border: '1px solid #2f3a44', color: '#8fd0ff' },
  badgeCustom: { display: 'inline-block', marginLeft: '8px', padding: '1px 6px', background: '#26324a', color: '#9ec5ff', border: '1px solid #3a5178', borderRadius: '8px', fontSize: '10px', fontWeight: 600 },
  badgeProject: { display: 'inline-block', marginLeft: '6px', padding: '1px 6px', background: '#173a24', color: '#a7e0b8', border: '1px solid #2e7d4f', borderRadius: '8px', fontSize: '10px', fontWeight: 600 },
  muted: { color: '#8a98a3', fontSize: '12.5px' },
  statusValuesRow: { display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0 8px', flexWrap: 'wrap' },
  hintInline: { fontSize: '11.5px', color: '#8a98a3', lineHeight: 1.4 },
  errorInline: { fontSize: '12px', color: '#ffcdd2', background: '#3a1414', border: '1px solid #ef5350', borderRadius: '4px', padding: '6px 10px', margin: '0 0 8px' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '0 0 10px' },
  chip: { padding: '3px 9px', background: '#26324a', color: '#9ec5ff', border: '1px solid #3a5178', borderRadius: '10px', fontSize: '12px', cursor: 'pointer' },
  error: { padding: '10px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '12px 0' },
  notice: { padding: '10px 12px', background: '#12331f', border: '1px solid #2e7d4f', borderRadius: '4px', color: '#c8e6c9', margin: '12px 0', fontSize: '13px' },
  readyOk: { padding: '10px 12px', background: '#12331f', border: '1px solid #2e7d4f', borderRadius: '4px', color: '#c8e6c9', fontSize: '13px', lineHeight: 1.5 },
  readyPending: { padding: '10px 12px', background: '#33290f', border: '1px solid #b8860b', borderRadius: '4px', color: '#f0d9a0', fontSize: '13px', lineHeight: 1.5 },
  testResult: { marginTop: '14px', padding: '12px', background: '#0d1117', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '13px' },
  notes: { margin: '8px 0 0', paddingLeft: '18px', color: '#d6a860', fontSize: '12.5px' },
  badgeOk: { display: 'inline-block', padding: '2px 8px', background: '#1a4a1a', color: '#c8e6c9', border: '1px solid #4caf50', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
  badgeSkip: { display: 'inline-block', padding: '2px 8px', background: '#3a3320', color: '#f0d9a0', border: '1px solid #b8860b', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
  badgeFail: { display: 'inline-block', padding: '2px 8px', background: '#4a1a1a', color: '#ffcdd2', border: '1px solid #ef5350', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
};
